import React, { useState } from 'react';
import { Search, DoorOpen, PackageOpen, Boxes, Truck, HardHat, Sparkles, Loader2, CheckCircle2, CalendarCheck, XCircle, ImageOff } from 'lucide-react';
import { alertasApi, errorMsg } from '../alertas/alertasObra.api';
import { COMBO, SEGURIDAD, MODULOS_PRO, olvidarCombo } from './comboIA';
import imgBuscador from '../../../assets/combo-ia/buscador.jpg';
import imgEntradas from '../../../assets/combo-ia/entradas.jpg';
import imgMateriales from '../../../assets/combo-ia/materiales.jpg';
import imgAcopio from '../../../assets/combo-ia/acopio.jpg';

/* Pantalla de activación de IA Pro (se ve en lugar de la página cuando la cámara no lo tiene).
   Sin tarjeta: el cliente acepta que se analice su obra con IA y queda 30 días gratis (POST /combo-ia/activar,
   que avisa por mail a Lisual). La obra se empieza a analizar recién desde ese momento.
   Las imágenes son capturas reales recortadas sin nombres de clientes. */

const INCLUYE = [
  { key: 'Buscador de obra', alias: ['Maquinaria'], Icon: Search, titulo: 'Buscador de obra', texto: 'Encontrá en segundos cuándo hubo un camión, una excavadora o una bomba de hormigón, con la foto.', img: imgBuscador },
  { key: 'Entradas y salidas', Icon: DoorOpen, titulo: 'Entradas y salidas', texto: 'Qué vehículos entraron, a qué hora y cuánto se quedaron. Línea de tiempo del día y PDF.', img: imgEntradas },
  { key: 'Materiales', Icon: PackageOpen, titulo: 'Materiales', texto: 'Cada llegada de material con foto de antes y después: arena, maderas, hierros, pallets.', img: imgMateriales },
  { key: 'Acopio', Icon: Boxes, titulo: 'Acopio', texto: 'Cómo crecen o bajan las pilas de material día a día, con aviso cuando cambian.', img: imgAcopio },
  { key: 'Alertas de entregas', Icon: Truck, titulo: 'Alertas de entregas', texto: '"Avisame cuando…": te escribimos por WhatsApp o mail cuando llega un camión o material.', img: null },
];

const MiniWhatsapp = () => (
  <div className="h-full w-full bg-[#e5ddd5] p-3 flex flex-col justify-end gap-2" aria-hidden="true">
    <div className="self-start max-w-[85%] rounded-lg rounded-tl-none bg-white px-3 py-2 text-[11px] leading-snug text-gray-800 shadow-sm">
      🚚 <b>Llegó material a la obra</b><br />Entró un camión a las 10:42 y dejó pallets junto al portón. Mirá la foto de antes y después 👇
      <div className="mt-1.5 grid grid-cols-2 gap-1">
        <div className="h-10 rounded bg-gradient-to-br from-stone-200 to-stone-300 flex items-end"><span className="m-0.5 text-[8px] bg-white/80 rounded px-1 text-gray-600">Antes</span></div>
        <div className="h-10 rounded bg-gradient-to-br from-amber-200 to-amber-400 flex items-end"><span className="m-0.5 text-[8px] bg-white/80 rounded px-1 text-gray-600">Después</span></div>
      </div>
      <div className="text-right text-[9px] text-gray-400 mt-0.5">10:43</div>
    </div>
  </div>
);

/** Adicional sobre IA Pro: se activa aparte (Seguridad e Higiene › pantalla de activación propia). */
export const ADICIONAL_SEGURIDAD = { key: 'Seguridad e Higiene', alias: ['IA'], Icon: HardHat, titulo: `Seguridad e Higiene · +USD ${SEGURIDAD.precioUsd}/mes`, texto: 'Uso de casco y chaleco verificado persona por persona, quién estuvo en obra y zonas de riesgo. Se activa aparte, con 30 días gratis.', img: null, Mini: 'seguridad' };

const MiniSeguridad = () => (
  <div className="h-full w-full bg-gradient-to-br from-slate-50 to-emerald-50 p-3 flex flex-col justify-between" aria-hidden="true">
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold text-gray-700">Uso de casco</span>
      <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2">92%</span>
    </div>
    <div className="flex items-end gap-1 h-14">
      {[88, 95, 72, 91, 97, 64, 93].map((v, i) => <div key={i} className="flex-1 rounded-t" style={{ height: `${v}%`, background: v >= 90 ? '#10b981' : v >= 75 ? '#f59e0b' : '#ef4444' }} />)}
    </div>
    <div className="flex gap-1.5">
      {['08:02 → 17:10', '08:15 → 12:40'].map((t) => <span key={t} className="text-[9px] bg-white rounded px-1.5 py-0.5 text-gray-600 shadow-sm">👷 {t}</span>)}
    </div>
  </div>
);

export const Tarjeta = ({ item, destacada }) => {
  const [rota, setRota] = useState(false);
  const { Icon } = item;
  return (
    <div className={`rounded-2xl border bg-white overflow-hidden flex flex-col ${destacada ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-gray-200'}`}>
      <div className="h-36 sm:h-40 bg-gray-100 overflow-hidden relative">
        {item.img && !rota ? (
          <img src={item.img} alt={`Ejemplo de ${item.titulo}`} loading="lazy" onError={() => setRota(true)} className="w-full h-full object-cover object-top" />
        ) : item.img ? (
          <div className="w-full h-full flex items-center justify-center text-gray-400"><ImageOff size={22} /></div>
        ) : item.Mini === 'seguridad' ? (
          <MiniSeguridad />
        ) : (
          <MiniWhatsapp />
        )}
        {destacada && <span className="absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wide bg-emerald-600 text-white rounded-full px-2 py-0.5">Estás acá</span>}
      </div>
      <div className="p-3 sm:p-4 flex-1">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"><Icon size={15} /></span>
          <h4 className="text-sm font-semibold text-gray-800">{item.titulo}</h4>
        </div>
        <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">{item.texto}</p>
      </div>
    </div>
  );
};

const CONSENTIMIENTO = 'Acepto que Lisual analice con inteligencia artificial las fotos de esta cámara a partir de este momento, durante 30 días gratis y sin tarjeta.';

const ComboIAVenta = ({ feedID, pagina, estado, onSimularAlta, onActivado }) => {
  const [ocupado, setOcupado] = useState(false);
  const [acepto, setAcepto] = useState(false);
  const [aviso, setAviso] = useState(null);
  const dias = estado?.trialDays || COMBO.diasPrueba;
  const usada = !!estado?.pro?.pruebaUsada;
  const desdeSeguridad = pagina === ADICIONAL_SEGURIDAD.key || (ADICIONAL_SEGURIDAD.alias || []).includes(pagina);
  const actual = INCLUYE.find((i) => i.key === pagina || (i.alias || []).includes(pagina));
  const orden = actual ? [actual, ...INCLUYE.filter((i) => i !== actual)] : INCLUYE;
  const precio = estado?.priceUsd || COMBO.precioUsd;

  const activar = async () => {
    if (!acepto) return;
    setAviso(null);
    if (estado?.simulado) {
      setAviso({ tipo: 'info', txt: 'Vista previa local: no se activa nada. Así se ve con IA Pro activa.' });
      setTimeout(() => onSimularAlta && onSimularAlta(), 900);
      return;
    }
    setOcupado(true);
    try {
      const nuevo = await alertasApi.comboActivar(feedID, pagina);
      olvidarCombo(feedID);
      if (onActivado) onActivado(nuevo);
    } catch (e) {
      setAviso({ tipo: 'error', txt: errorMsg(e) });
      setOcupado(false);
    }
  };

  return (
    <div className="space-y-4" data-combo-venta>
      <div className="rounded-2xl bg-gradient-to-br from-gray-900 via-gray-900 to-indigo-900 text-white p-5 sm:p-7 overflow-hidden relative">
        <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-indigo-400/10 blur-2xl" aria-hidden="true" />
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide bg-gradient-to-r from-indigo-500 to-violet-500 text-white rounded-full px-2.5 py-1">
          <Sparkles size={13} />PRO
        </span>
        <h3 className="mt-3 text-xl sm:text-2xl font-bold leading-tight max-w-xl">
          {desdeSeguridad ? `Seguridad e Higiene se suma a ${COMBO.nombre}` : actual ? `${actual.titulo} es parte de ${COMBO.nombre}` : `Activá ${COMBO.nombre} en esta cámara`}
        </h3>
        <p className="mt-2 text-sm text-gray-300 max-w-xl">
          {desdeSeguridad
            ? `Primero activá ${COMBO.nombre} en esta cámara. Después podés sumar Seguridad e Higiene (USD ${SEGURIDAD.precioUsd} por mes), también con ${SEGURIDAD.diasPrueba} días gratis.`
            : 'La IA mira las fotos de tu cámara y te ordena qué pasó en la obra: vehículos, máquinas, materiales, acopio y alertas de entregas.'}
        </p>
        <div className="mt-4 rounded-xl bg-white/10 px-4 py-3 text-sm max-w-xl" data-combo-modulos>
          <div className="font-semibold text-white">Durante los {dias} días gratis tenés:</div>
          <ul className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-gray-200">
            {MODULOS_PRO.map((m) => <li key={m} className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-300 shrink-0" />{m}</li>)}
          </ul>
          <div className="mt-2 text-gray-300">Después: <b className="text-white">USD {precio} por mes por cámara</b>. Cancelás cuando quieras.</div>
        </div>

        {usada ? (
          <p className="mt-5 text-sm rounded-xl bg-white/10 px-4 py-3 max-w-xl" data-combo-usada>
            Esta cámara ya usó sus {dias} días gratis de {COMBO.nombre}. Escribinos y lo reactivamos.
          </p>
        ) : (
          <div className="mt-5 space-y-3 max-w-xl">
            <div className="text-sm text-indigo-200 font-semibold">{dias} días gratis · sin tarjeta · sin compromiso</div>
            <div className="rounded-xl bg-white/10 px-4 py-3 text-sm text-gray-200">
              <b className="text-white">Tu obra se empieza a analizar recién cuando lo activás.</b> Hasta ese momento no analizamos las fotos de esta cámara con IA.
              Al activarlo procesamos también los últimos 60 días, así ves resultados desde el primer día.
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-100 cursor-pointer">
              <input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} className="mt-0.5 h-4 w-4 accent-indigo-400" data-combo-acepto />
              <span>{estado?.pro?.consentimiento || CONSENTIMIENTO}</span>
            </label>
            <button
              type="button"
              onClick={activar}
              disabled={ocupado || !acepto}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-400 hover:bg-indigo-300 text-gray-900 font-semibold px-5 py-3 text-sm disabled:opacity-50 transition-colors"
              data-combo-activar
            >
              {ocupado ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}Activar {COMBO.nombre} gratis
            </button>
          </div>
        )}
        {aviso && (
          <p className={`mt-3 text-xs rounded-lg px-3 py-2 ${aviso.tipo === 'error' ? 'bg-red-500/15 text-red-200' : 'bg-white/10 text-gray-200'}`}>{aviso.txt}</p>
        )}
      </div>

      <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-combo-garantias>
        {[[CalendarCheck, `${dias} días gratis para probarlo con tu obra`], [XCircle, 'Sin tarjeta y sin compromiso'], [CheckCircle2, 'El análisis empieza al activarlo']].map(([Icon, t]) => (
          <li key={t} className="flex items-center gap-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-xl px-3 py-2.5"><Icon size={15} className="text-emerald-600 shrink-0" />{t}</li>
        ))}
      </ul>

      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2 px-1">Qué incluye</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {orden.map((it) => <Tarjeta key={it.key} item={it} destacada={it === actual} />)}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2 px-1">Adicional</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Tarjeta item={ADICIONAL_SEGURIDAD} destacada={desdeSeguridad} />
        </div>
      </div>

      <p className="text-[11px] text-gray-500 px-1">
        Las imágenes son ejemplos de otras obras. Las alertas de entregas todavía están en proceso: la IA puede equivocarse o no ver una entrega, y la vamos ajustando con tus 👍/👎.
      </p>
    </div>
  );
};

export default ComboIAVenta;
